import { Box, Flex, FlexProps, Input, SystemStyleObject, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';

import { CloseIcon, SearchIcon } from '../../../components';
import { customScrollbarStyles, pxToRem, TRANSITIONS } from '../../../theme';
import { StringTraitsWithCountType, useKollections } from '../KollectionsProvider';

function searchFilters(
  filters: StringTraitsWithCountType[],
  searchInput: string
): StringTraitsWithCountType[] {
  return filters.reduce((acc, item) => {
    const { traitType, values } = item;
    const query = searchInput.toLowerCase();

    if (traitType.toLowerCase().indexOf(query) > -1) {
      acc.push(item);
    } else {
      const filteredValues = values.filter(({ value }) => value.toLowerCase().indexOf(query) > -1);

      if (filteredValues.length > 0) {
        acc.push({ traitType, values: filteredValues });
      }
    }

    return acc;
  }, [] as StringTraitsWithCountType[]);
}

export function KollectionsSearchBox({
  styles,
}: {
  styles?: {
    container?: SystemStyleObject;
    searchedFiltersContainer?: SystemStyleObject;
    searchedFiltersItem?: FlexProps;
  };
}) {
  const { setStringTrait, kollectionStringTraits } = useKollections();
  const [searchInput, setSearchInput] = useState('');
  const searchedFilters = useMemo(() => {
    if (searchInput === '') {
      return [];
    } else {
      return searchFilters(kollectionStringTraits, searchInput);
    }
  }, [kollectionStringTraits, searchInput]);

  return (
    <Box position="relative" zIndex={2} {...styles?.container}>
      <Flex position="relative">
        <SearchIcon
          position="absolute"
          zIndex={1}
          top="50%"
          transform="translateY(-50%)"
          left={3}
          width={pxToRem(18)}
        />
        <Input
          onChange={(e) => setSearchInput(e.target.value)}
          value={searchInput}
          placeholder="Search Traits"
          size="sm"
          pl={10}
          bg="transparent"
          _placeholder={{
            color: 'inherit',
          }}
        />
        <CloseIcon
          onClick={searchInput ? () => setSearchInput('') : undefined}
          cursor={searchInput ? 'pointer' : 'default'}
          position="absolute"
          top="50%"
          right={3}
          transform="translateY(-50%)"
          transition={TRANSITIONS.standard}
          opacity={searchInput ? 1 : 0}
          width={pxToRem(22)}
        />
      </Flex>
      {searchedFilters.length > 0 ? (
        <Flex
          position="absolute"
          alignItems="flex-start"
          flexDirection="column"
          bg="backgroundSecondary"
          w="100%"
          bottom={-3}
          left={0}
          transform="translateY(100%)"
          h={['auto', '42vh']}
          maxH={pxToRem(500)}
          overflow="auto"
          py={3}
          gap={2}
          border="thin"
          borderRadius="sm"
          css={{
            ...customScrollbarStyles,
            ...styles?.searchedFiltersContainer,
          }}
        >
          {searchedFilters.map(({ traitType, values }) => (
            <Box key={traitType} w="100%">
              <Box as="strong" key={traitType} px={4}>
                {traitType}
              </Box>
              {values.map(({ value, count }) => (
                <Flex
                  onClick={() => setStringTrait({ traitType, value })}
                  key={`${traitType} - ${value}`}
                  justifyContent="space-between"
                  alignItems="center"
                  cursor="pointer"
                  px={6}
                  py={2}
                  transition={TRANSITIONS.standard}
                  _hover={{
                    bg: 'background',
                  }}
                  {...styles?.searchedFiltersItem}
                >
                  {value}
                  <Text textStyle="p-sm" color="textTertiary">
                    {count}
                  </Text>
                </Flex>
              ))}
            </Box>
          ))}
        </Flex>
      ) : null}
    </Box>
  );
}
